/**
 * ChatInput — textarea + send button.
 * Enter sends; Shift+Enter inserts newline.
 * Auto-focuses when streaming ends (disabled → enabled transition).
 */
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Input, Button, theme } from 'antd';
import { SendOutlined } from '@ant-design/icons';

const { TextArea } = Input;

interface Props {
  onSend: (content: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ChatInput({ onSend, disabled, placeholder }: Props) {
  const { token } = theme.useToken();
  const [value, setValue] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const prevDisabled = useRef(disabled);

  // Focus when streaming ends (disabled flips false)
  useEffect(() => {
    if (prevDisabled.current && !disabled) {
      // Small delay so the DOM re-enables first
      setTimeout(() => {
        textAreaRef.current?.focus();
      }, 80);
    }
    prevDisabled.current = disabled;
  }, [disabled]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 8,
        padding: '12px 16px',
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
      }}
    >
      <TextArea
        ref={(node) => {
          // Ant Design TextArea ref hack to reach the underlying textarea
          if (node) {
            const el = (node as unknown as { resizableTextArea?: { textArea?: HTMLTextAreaElement } })
              ?.resizableTextArea?.textArea;
            if (el) (textAreaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
          }
        }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? 'Pergunte ao Delfos…'}
        autoSize={{ minRows: 1, maxRows: 5 }}
        disabled={disabled}
        style={{ flex: 1, borderRadius: token.borderRadiusSM, resize: 'none' }}
        data-tour="ai-chat-input"
      />
      <Button
        type="primary"
        shape="circle"
        icon={<SendOutlined />}
        onClick={submit}
        disabled={disabled || !value.trim()}
        data-tour="ai-chat-send"
      />
    </div>
  );
}
